import { Col, Form, Input, Row } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { GENDERS } from '../../../../../common/constants';
import { formValidatorRules } from '../../../../../common/utils';
import CountrySelect from '../../../../../components/CountrySelect';
import PincodeSelectName from '../../../../../components/PincodeSelectName';
import RevealPiiButton from '../../../../../components/RevealPiiButton';
import SelectComponent from '../../../../../components/SelectComponent';
import usePiiReveal from '../../../../../hooks/usePiiReveal';

const { Item } = Form;
const {
  required,
  email,
  name,
  phone,
  whitespaceNotAllowed,
} = formValidatorRules;

const EndUserForm = ({
  form,
  onFinish,
  handleShowPrompt,
  initialValues,
  isView,
  endUserDetail,
  userId,
}) => {
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [countryId, setCountryId] = useState('');
  const {
    hasRevealPermission,
    isPiiMaskingBypassed,
    revealedPii,
    isRevealing,
    reveal,
  } = usePiiReveal({ userId });
  const isPiiRevealed = !!revealedPii;
  const revealedPiiRef = useRef(revealedPii);
  revealedPiiRef.current = revealedPii;

  const getPiiFieldValues = () => {
    const revealed = revealedPiiRef?.current;
    const piiValues = {};
    if (!form?.isFieldTouched('email')) {
      piiValues.email = revealed ? revealed?.email : initialValues?.email;
    }
    if (!form?.isFieldTouched('contact')) {
      piiValues.contact = revealed
        ? revealed?.contactNo
        : initialValues?.contact;
    }
    return piiValues;
  };

  useEffect(() => {
    const initialValueFun = () => {
      const { email: initialEmail, contact: initialContact, ...restValues } =
        initialValues || {};
      form?.setFieldsValue({
        ...restValues,
        country: countryId,
        ...getPiiFieldValues(),
      });
    };
    setTimeout(initialValueFun, 300);
  }, [countryId]);

  useEffect(() => {
    if (revealedPii) {
      form?.setFieldsValue({
        email: revealedPii?.email,
        contact: revealedPii?.contactNo,
      });
    }
  }, [revealedPii]);

  useEffect(() => {
    if (revealedPii) {
      return;
    }
    form?.setFieldsValue(getPiiFieldValues());
  }, [initialValues?.email, initialValues?.contact, revealedPii]);

  const isPiiHidden = !isPiiRevealed && !isPiiMaskingBypassed;
  const isEmailMasked = !!initialValues?.email && isPiiHidden;
  const isContactMasked = !!initialValues?.contact && isPiiHidden;

  const renderPiiLabel = (label, isMasked) => (
    <span className="pii-form-label">
      {label}
      {hasRevealPermission && userId && isMasked ? (
        <RevealPiiButton onClick={reveal} isRevealing={isRevealing} />
      ) : null}
    </span>
  );

  const onFinishFailed = () => {
    setValidationTriggered(true);
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onFinish}
      onFieldsChange={handleShowPrompt}
      disabled={!!isView}
      onFinishFailed={onFinishFailed}
      className={isView ? 'common-view-form' : 'form'}
      validateTrigger={validationTriggered ? 'onChange' : 'onSubmit'}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Item
            label="First Name"
            name="firstName"
            rules={[
              { ...required, message: 'Please Enter First Name!' },
              name,
              whitespaceNotAllowed,
            ]}
          >
            <Input allowClear placeholder="Enter First Name" maxLength={50} />
          </Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Item
            label="Last Name"
            name="lastName"
            rules={[
              { ...required, message: 'Please Enter Last Name!' },
              name,
              whitespaceNotAllowed,
            ]}
          >
            <Input allowClear placeholder="Enter Last Name" maxLength={50} />
          </Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Item label="User ID" name="id" rules={[required]}>
            <Input disabled />
          </Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Item
            label={renderPiiLabel('Email ID', isEmailMasked)}
            name="email"
            rules={
              isEmailMasked
                ? []
                : [
                    { ...required, message: 'Please Enter Email!' },
                    email,
                    whitespaceNotAllowed,
                  ]
            }
          >
            <Input
              allowClear
              placeholder="Enter Email"
              maxLength={255}
              disabled={isEmailMasked}
            />
          </Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Item label="Gender" name="gender">
            <SelectComponent placeholder="Select Gender" options={GENDERS} />
          </Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Item
            label={renderPiiLabel('Contact Details', isContactMasked)}
            name="contact"
            rules={
              isContactMasked
                ? []
                : [
                    {
                      ...required,
                      message: 'Please Enter Contact Details!',
                    },
                    phone,
                  ]
            }
          >
            <Input
              allowClear
              placeholder="Enter Contact Details"
              maxLength={10}
              disabled={isContactMasked}
            />
          </Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Item label="Mapped Library" name="mappedLibrary">
            <Input disabled />
          </Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Item label="Landmark" name="landmark" rules={[whitespaceNotAllowed]}>
            <Input allowClear placeholder="Enter Landmark" maxLength={50} />
          </Item>
        </Col>
        {endUserDetail?.latLong?.coordinates?.[0] &&
          endUserDetail?.latLong?.coordinates?.[1] && (
            <>
              <Col xs={24} sm={12}>
                <Item label="LAT" name="lat">
                  <Input allowClear placeholder="Enter LAT" disabled />
                </Item>
              </Col>
              <Col xs={24} sm={12}>
                <Item label="LNG" name="lng">
                  <Input allowClear placeholder="Enter LNG" disabled />
                </Item>
              </Col>
            </>
          )}
        <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
          <Item label="Address Line-1" name="address" rules={[whitespaceNotAllowed]}>
            <Input allowClear placeholder="Enter Address Line-1" maxLength={250} />
          </Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
          <Item label="Address Line-2" name="address2" rules={[whitespaceNotAllowed]}>
            <Input allowClear placeholder="Enter Address Line-2" maxLength={250} />
          </Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
          <Item label="Locality" name="locality" rules={[whitespaceNotAllowed]}>
            <Input allowClear placeholder="Enter Locality" maxLength={250} />
          </Item>
        </Col>
        <Col xs={24} sm={12} lg={24}>
          <Item label="Additional Address" name="additionalAddress">
            <Input placeholder="Enter Additional Address" disabled />
          </Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Form.Item
            label="Country"
            name="country"
            rules={[{ required: true, message: 'Please Select Country!' }]}
          >
            <CountrySelect setCountryId={setCountryId} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Form.Item label="State" name="state" rules={[whitespaceNotAllowed]}>
            <Input allowClear placeholder="Enter State" maxLength={50} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Form.Item label="City" name="city" rules={[whitespaceNotAllowed]}>
            <Input allowClear placeholder="Enter City" maxLength={50} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6} xxl={6}>
          <Form.Item label="Pincode" name="pincode">
            <PincodeSelectName userData={endUserDetail} disabled />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default EndUserForm;
